import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingDown, Calculator } from "lucide-react";
import { motion } from "framer-motion";

const fmt = (n) => `$${Math.round(n / 50) * 50 >= 1000 ? (Math.round(n / 50) * 50).toLocaleString() : Math.round(n / 50) * 50}`;
const fmtCompact = (n) => {
  if (n < 100000) return fmt(n);
  const rounded = Math.round(n / 100) / 10;
  return `$${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}K`;
};

const profiles = {
  tidy: {
    label: "Tidy",
    note: "Few vendors",
    low: 0.055,
    high: 0.11,
    leakage: [18, 24, 31],
  },
  mixed: {
    label: "Mixed",
    note: "Several contracts",
    low: 0.085,
    high: 0.18,
    leakage: [23, 29, 35],
  },
  scattered: {
    label: "Scattered",
    note: "Many sites or teams",
    low: 0.12,
    high: 0.265,
    leakage: [27, 33, 40],
  },
};

const spendFactor = (spend) => Math.min(1.18, Math.max(0.86, 0.82 + Math.log10(spend / 2500) * 0.18));

export const SavingsEstimator = ({ onCta }) => {
  const [spend, setSpend] = useState(22500);
  const [profileKey, setProfileKey] = useState("mixed");

  const estimate = useMemo(() => {
    const profile = profiles[profileKey];
    const factor = spendFactor(spend);
    const lowRate = profile.low * factor;
    const highRate = Math.min(0.31, profile.high * factor);
    const lowMonthly = spend * lowRate;
    const highMonthly = spend * highRate;
    const midpoint = (lowMonthly + highMonthly) / 2;

    return {
      lowMonthly,
      highMonthly,
      lowAnnual: lowMonthly * 12,
      highAnnual: highMonthly * 12,
      confidence: Math.round(lowRate * 100) + "-" + Math.round(highRate * 100) + "%",
      billing: midpoint * (profile.leakage[0] / 100),
      contracts: midpoint * (profile.leakage[1] / 100),
      unused: midpoint * (profile.leakage[2] / 100),
    };
  }, [spend, profileKey]);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-primary/25 bg-gradient-surface p-7 shadow-elegant sm:p-9">
      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 text-accent">
            <Calculator className="h-6 w-6" />
          </span>
          <div>
            <span className="kicker">Instant Savings Estimate</span>
            <h3 className="font-display text-2xl font-semibold">Size the opportunity</h3>
          </div>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Adjust spend and operating complexity. The range moves like a real audit model: cleaner environments
          show tighter recovery, while scattered vendors and locations create more places for waste to hide.
        </p>

        <div className="mt-8">
          <div className="flex items-end justify-between gap-4">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Monthly telecom, SaaS & cloud spend
            </span>
            <span className="font-display text-2xl font-semibold text-foreground">
              {fmt(spend)}
              {spend >= 125000 && <span className="text-primary">+</span>}
              <span className="ml-1 text-sm font-normal text-muted-foreground">/mo</span>
            </span>
          </div>
          <Slider
            value={[spend]}
            onValueChange={([v]) => setSpend(v)}
            min={2500}
            max={125000}
            step={2500}
            className="mt-4"
            aria-label="Monthly technology spend"
          />
          <div className="mt-2 flex justify-between font-mono text-[11px] text-muted-foreground">
            <span>$2.5K</span>
            <span>$125K+</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-2">
          {Object.entries(profiles).map(([key, profile]) => (
            <button
              key={key}
              type="button"
              onClick={() => setProfileKey(key)}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                profileKey === key
                  ? "border-primary bg-primary/15 text-foreground"
                  : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              <span className="block text-sm font-medium">{profile.label}</span>
              <span className="block text-[11px]">{profile.note}</span>
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ResultCard label="Likely monthly recovery" low={estimate.lowMonthly} high={estimate.highMonthly} spend={spend} />
          <ResultCard label="Annualized opportunity" low={estimate.lowAnnual} high={estimate.highAnnual} spend={spend} highlight compact />
        </div>

        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between font-mono text-xs text-muted-foreground">
            <span>Typical first-pass range</span>
            <span>{estimate.confidence}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <motion.div
              className="h-full rounded-full bg-gradient-primary"
              initial={false}
              animate={{ width: `${Math.min(72, 24 + estimate.highMonthly / spend * 150)}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <MiniMetric label="Billing" value={estimate.billing} />
            <MiniMetric label="Contracts" value={estimate.contracts} />
            <MiniMetric label="Unused" value={estimate.unused} />
          </div>
          <p className="flex items-center gap-1.5 font-mono text-xs text-success">
            <TrendingDown className="h-3.5 w-3.5" /> Real invoices usually move the range up or down after review.
          </p>
        </div>

        <Button variant="premium" size="lg" className="mt-8 w-full" onClick={onCta}>
          Confirm It With a Free Audit <ArrowRight className="h-4 w-4" />
        </Button>
        <p className="mt-3 text-center font-mono text-xs text-muted-foreground">
          Estimate only. Actual recovery depends on contracts, usage, inventory, and invoice history.
        </p>
      </div>
    </div>
  );
};

const ResultCard = ({ label, low, high, spend, highlight, compact }) => (
  <div className={`min-w-0 rounded-2xl border px-4 py-5 ${highlight ? "border-primary/40 bg-primary/10" : "border-border bg-card/60"}`}>
    <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-2 flex min-w-0 items-baseline gap-x-1 whitespace-nowrap font-display text-[clamp(0.9rem,1.45vw,1.25rem)] font-semibold leading-tight text-foreground">
      <span>{compact ? fmtCompact(low) : fmt(low)}{spend >= 125000 && "+"}</span>
      <span className="text-muted-foreground">to</span>
      <span>{compact ? fmtCompact(high) : fmt(high)}{spend >= 125000 && "+"}</span>
    </div>
  </div>
);

const MiniMetric = ({ label, value }) => (
  <div className="rounded-md border border-border bg-card/50 px-3 py-2">
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-semibold text-foreground">{fmt(value)}</div>
  </div>
);
